import { toast } from '@avila-roffe/components-web';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createTool, deleteTool, getToolById, listTools } from '../toolApi';
import type { Tool } from '../types';

// ─── Query key factory ────────────────────────────────────────────────────────

export const toolKeys = {
  all: ['tools'] as const,
  lists: () => [...toolKeys.all, 'list'] as const,
  detail: (id: string) => [...toolKeys.all, 'detail', id] as const,
};

// ─── List ─────────────────────────────────────────────────────────────────────

export function useTools(limit = 50) {
  return useQuery({
    queryKey: toolKeys.lists(),
    queryFn: () => listTools(limit),
    placeholderData: keepPreviousData,
  });
}

// ─── Detail ───────────────────────────────────────────────────────────────────

export function useTool(id: string | undefined) {
  return useQuery({
    queryKey: toolKeys.detail(id ?? ''),
    enabled: !!id,
    queryFn: () => getToolById(id as string),
  });
}

// ─── Create ───────────────────────────────────────────────────────────────────

export function useCreateTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      name: string;
      description?: string;
      scope?: import('../types.js').ToolScope;
      status?: import('../types.js').ToolStatus;
    }) => createTool(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: toolKeys.lists() });
      toast.success('Tool created');
    },
  });
}

// ─── Delete ───────────────────────────────────────────────────────────────────

export function useDeleteTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteTool(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: toolKeys.lists() });
      toast.success('Tool deleted');
    },
  });
}

export type { Tool };
